// frontend/src/hooks/useSelfieCamera.js
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Selfie camera hook (mirror-only).
 * Android Chrome notes:
 * - getUserMedia + element.play() must happen inside the original user gesture
 *   chain. Doing play() inside a useEffect (after re-render) can be silently
 *   rejected by Android. We therefore attach srcObject + call play() inline,
 *   *before* any setState, while the gesture is still hot.
 * - We also keep a useEffect for re-attachment if the element remounts.
 */
export function useSelfieCamera() {
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const [stream, setStream] = useState(null);
  const [isCameraOn, setIsCameraOn] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const [error, setError] = useState(null);

  // Keep streamRef in sync so stopStream is stable
  useEffect(() => {
    streamRef.current = stream;
  }, [stream]);

  const stopStream = useCallback(() => {
    try {
      const s = streamRef.current;
      if (s) s.getTracks().forEach((t) => t.stop());
    } catch {
      // no-op
    } finally {
      const el = videoRef.current;
      if (el) {
        try { el.pause(); } catch {}
        el.srcObject = null;
      }
      setStream(null);
      setIsCameraOn(false);
    }
  }, []);

  // Critical helper: assigns the stream and starts playback while we're still
  // inside the user-gesture chain. Returns true on successful play.
  const attachAndPlay = useCallback(async (mediaStream) => {
    const el = videoRef.current;
    if (!el) return false;

    el.srcObject = mediaStream;
    el.muted = true;
    el.autoplay = true;
    el.playsInline = true;
    el.setAttribute("playsinline", "");
    el.setAttribute("webkit-playsinline", "");

    try {
      const p = el.play();
      if (p && typeof p.then === "function") await p;
      return true;
    } catch (err) {
      console.warn("[selfie] inline play failed; will retry", err);
      // Schedule retries (Android sometimes needs the metadata first)
      const retry = () => {
        try { el.play()?.catch(() => {}); } catch {}
      };
      requestAnimationFrame(retry);
      setTimeout(retry, 250);
      setTimeout(retry, 800);
      return false;
    }
  }, []);

  const startStream = useCallback(async () => {
    if (isRequesting || isCameraOn) return;

    setIsRequesting(true);
    setError(null);

    try {
      const isLocalhost =
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1";

      if (!isLocalhost && window.location.protocol !== "https:") {
        throw new Error("Camera requires HTTPS. Open the app on a secure (https) domain.");
      }

      if (!navigator?.mediaDevices?.getUserMedia) {
        throw new Error("Camera API not supported in this browser.");
      }

      // Timeout safety
      const timeoutMs = 8000;
      const timeout = new Promise((_, reject) =>
        setTimeout(
          () => reject(new Error("Camera request timed out. Check site permissions and try again.")),
          timeoutMs
        )
      );

      const tryGetMedia = async () => {
        try {
          return await navigator.mediaDevices.getUserMedia({
            video: { facingMode: { ideal: "user" } },
            audio: false
          });
        } catch (e1) {
          console.warn("[selfie] facingMode failed; falling back to video:true", e1);
          return await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        }
      };

      const media = await Promise.race([tryGetMedia(), timeout]);

      // ===== ATTACH + PLAY SYNCHRONOUSLY (still in gesture chain) =====
      await attachAndPlay(media);

      // Now commit React state (re-render). The useEffect below will only
      // re-attach if the video element identity changed.
      setStream(media);
      setIsCameraOn(true);
    } catch (err) {
      console.error("[selfie] getUserMedia failed", err);
      setError(err?.message || "Could not access your camera. Please check browser permissions.");
      setIsCameraOn(false);
      setStream(null);
    } finally {
      setIsRequesting(false);
    }
  }, [isRequesting, isCameraOn, attachAndPlay]);

  // Re-attachment safety: if the video element remounts after we already have a
  // stream (e.g., layout change), reattach. This does NOT replace inline attach.
  useEffect(() => {
    const el = videoRef.current;
    if (!el || !stream) return;

    if (el.srcObject !== stream) {
      el.srcObject = stream;
      el.muted = true;
      el.autoplay = true;
      el.playsInline = true;
    }

    const tryPlay = () => {
      try { el.play()?.catch(() => {}); } catch {}
    };

    // Hook into loadedmetadata as a final safety net
    const onLoaded = () => tryPlay();
    el.addEventListener("loadedmetadata", onLoaded);

    requestAnimationFrame(tryPlay);
    setTimeout(tryPlay, 250);

    return () => {
      el.removeEventListener("loadedmetadata", onLoaded);
    };
  }, [stream]);

  // Cleanup on unmount
  useEffect(() => {
    return () => stopStream();
  }, [stopStream]);

  return {
    videoRef,
    stream,
    isCameraOn,
    isRequesting,
    error,
    startStream,
    stopStream
  };
}
